import React from 'react';
import styled, { css } from 'styled-components';
import {useTranslations} from "use-intl";

import {
  FETCH_STATUSES,
  generateNumberArray,
  Nullable,
  ResourceType,
} from '@tager/web-core';
import { PaginatedResourceType } from '@tager/web-core/src/typings/common';

import { CATEGORY_TYPE } from '@/modules/Catalog/types';
import Top from '@/modules/Catalog/components/Top';
import Spinner from '@/components/Spinner';
import Relations from '@/components/Relations';
import RelationsExtended from '@/modules/Catalog/components/RelationsExtended/RelationsExtended';
import Link from '@/components/Link';
import FilterBtn from '@/modules/Catalog/components/Filter/FilterBtn';
import SortBtn from '@/modules/Catalog/components/Filter/SortBtn';
import CatalogCard from '@/components/Cards/CatalogCard';
import Pagination from '@/components/Pagination';
import { QUERY_DEFAULT_VALUES } from '@/constants/catalog';
import ContentContainer from '@/components/ContentContainer';
import { Category } from '@/store/reducers/pages/catalog/catalog';
import {
  CatalogExtendedRelation,
  CategoryRelation,
  ProductType,
} from '@/typings/model';

type Props = {
  alias: string;
  category: Category;
  relations: ResourceType<Nullable<CategoryRelation[]>> | undefined;
  relationsExtended:
    | ResourceType<Nullable<CatalogExtendedRelation[]>>
    | undefined;
  products: PaginatedResourceType<ProductType> | undefined;
  pageAmount: number;
  activePage: number;
  isEmptyCatalog: boolean;
  onChange: (number: number) => void;
};

function CatalogView({
  alias,
  isEmptyCatalog,
  category,
  pageAmount,
  activePage,
  relationsExtended,
  relations,
  products,
  onChange,
}: Props) {
  const t = useTranslations();

  return (
    <Component>
      <ContentContainer>
        <Top
          count={products?.meta?.total}
          showPagesAmount={category.data?.type === CATEGORY_TYPE.product}
        />
        {relations?.status === FETCH_STATUSES.LOADING ? (
          <ContentWrapper isRelations>
            <Spinner />
          </ContentWrapper>
        ) : (
          relations?.status === FETCH_STATUSES.SUCCESS &&
          relations.data &&
          relations.data.length > 0 && <Relations data={relations.data} />
        )}
        {category?.data?.type === 'CATALOG' &&
          (relationsExtended?.status === FETCH_STATUSES.LOADING ? (
            <ContentWrapper>
              <Spinner />
            </ContentWrapper>
          ) : (
            relationsExtended?.status === FETCH_STATUSES.SUCCESS &&
            relationsExtended?.data &&
            relationsExtended.data.length > 0 && (
              <RelationsExtended data={relationsExtended?.data ?? []} />
            )
          ))}
        {category?.data?.type === 'PRODUCT' && (
          <>
            <RelationsShortLinkList>
              {relations?.data?.map((link) => (
                <LinkItem key={link.id}>
                  <Link to={'/catalog/' + link.urlPath}>{link.name}</Link>
                </LinkItem>
              ))}
            </RelationsShortLinkList>
            <Filters>
              <FilterBtn
                productId={category?.data?.id ?? 0}
                productAlias={alias}
                productsCount={products?.data?.length}
              />
              <SortBtn alias={alias} productId={category?.data?.id ?? 0} />
            </Filters>
          </>
        )}
        {category.data?.type === 'PRODUCT' &&
        products?.status === FETCH_STATUSES.LOADING ? (
          <ContentWrapper>
            {generateNumberArray(5).map((elem) => (
              <ProductsSpinnerWrapper key={elem}>
                <Spinner />
              </ProductsSpinnerWrapper>
            ))}
          </ContentWrapper>
        ) : (
          products?.status === FETCH_STATUSES.SUCCESS &&
          products.data &&
          products.data.length > 0 && (
            <ContentWrapper>
              {products?.data.map((product) => (
                <Link key={product.id} to={`/products/${product.urlAlias}`}>
                  <CatalogCard data={product} />
                </Link>
              ))}
            </ContentWrapper>
          )
        )}
        {isEmptyCatalog ? (
          <PlaceholderLabel>{t('search.products_not_found')}</PlaceholderLabel>
        ) : (
          <PaginationWrapper className="catalog-pagination-wrapper">
            <Pagination
              className="catalog-pagination"
              current={activePage}
              pageSize={QUERY_DEFAULT_VALUES.PageLimit}
              onChange={onChange}
              count={pageAmount}
            />
          </PaginationWrapper>
        )}
        <Content
          dangerouslySetInnerHTML={{
            __html: category?.data?.content ?? '',
          }}
        />
      </ContentContainer>
    </Component>
  );
}

export default CatalogView;

const Component = styled.div``;

const ContentWrapper = styled.div<{
  isForProducts?: boolean;
  isRelations?: boolean;
}>`
  display: grid;
  row-gap: 15px;
  margin-top: 30px;
  position: relative;

  ${({ isForProducts }) =>
    isForProducts
      ? css`
          min-height: auto;
        `
      : css`
          min-height: 100px;
        `}
  .catalog-spinner {
    height: 352px;
  }
  ${({ isRelations }) =>
    isRelations
      ? css`
          margin: 6px -15px 0;
          background: rgb(245, 245, 245);
        `
      : null}
`;

const Content = styled.div`
  margin-top: 30px;
  & > p:not(:first-child) {
    margin-top: 20px;
  }
`;

const PlaceholderLabel = styled.div`
  padding: 70px 0;
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  line-height: 30px;
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-width: 768px;
  margin: 12px auto 0;
`;

const RelationsShortLinkList = styled.ul`
  margin-top: 10px;
`;
const LinkItem = styled.li`
  font-weight: 600;
  font-size: 13px;
  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 30px;
`;

const ProductsSpinnerWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 382px;
`;
